import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Editor from '@monaco-editor/react';
import { Button } from '../../../components/ui/button';
import { Loader2, ArrowLeft, Save, File, Folder, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { themeApi, type ThemeAsset } from './themeApi';

export default function CodeEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedAssetKey, setSelectedAssetKey] = useState<string | null>(null);
  const [editorContent, setEditorContent] = useState<string>('');

  const { data: storeTheme, isLoading: loadingTheme } = useQuery({
    queryKey: ['store-themes', id],
    queryFn: () => themeApi.getInstalled(id as string),
    enabled: Boolean(id),
  });

  const selectedAsset = storeTheme?.assets?.find((a) => a.key === selectedAssetKey);

  const saveMutation = useMutation({
    mutationFn: ({ key, content }: { key: string, content: string }) =>
      themeApi.updateAsset(id as string, key, content),
    onSuccess: () => {
      toast.success('Asset saved successfully');
      queryClient.invalidateQueries({ queryKey: ['store-themes', id] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to save asset');
    }
  });

  const handleAssetSelect = (asset: ThemeAsset) => {
    setSelectedAssetKey(asset.key);
    setEditorContent(asset.content || '');
  };

  const handleSave = () => {
    if (!selectedAssetKey) return;
    saveMutation.mutate({ key: selectedAssetKey, content: editorContent });
  };

  if (loadingTheme) {
    return <div className="h-screen w-full flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  if (!storeTheme) {
    return <div className="p-8">Theme not found</div>;
  }

  // Group assets by folder structure
  const assets = storeTheme.assets ?? [];
  const layouts = assets.filter((a) => a.key.startsWith('layouts/'));
  const templates = assets.filter((a) => a.key.startsWith('templates/'));
  const sections = assets.filter((a) => a.key.startsWith('sections/'));
  const snippets = assets.filter((a) => a.key.startsWith('snippets/'));

  return (
    <div className="h-full w-full flex flex-col bg-background overflow-hidden">
      {/* Header Bar */}
      <header className="h-14 border-b flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/store/themes')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="font-medium">{storeTheme.theme.name} - Code Editor</div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => window.open(themeApi.previewUrl(id as string), '_blank')}
          >
            <ExternalLink className="mr-2 h-4 w-4" /> Preview
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={!selectedAssetKey || saveMutation.isPending || editorContent === selectedAsset?.content}
          >
            {saveMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save
          </Button>
        </div>
      </header>

      {/* Main Workspace */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar / File Tree */}
        <aside className="w-64 border-r bg-muted/20 flex flex-col overflow-y-auto">
          <div className="p-4 font-semibold text-sm border-b">Files</div>
          <div className="p-2 space-y-1">
            <FolderSection title="Layouts" assets={layouts} selectedKey={selectedAssetKey} onSelect={handleAssetSelect} />
            <FolderSection title="Templates" assets={templates} selectedKey={selectedAssetKey} onSelect={handleAssetSelect} />
            <FolderSection title="Sections" assets={sections} selectedKey={selectedAssetKey} onSelect={handleAssetSelect} />
            <FolderSection title="Snippets" assets={snippets} selectedKey={selectedAssetKey} onSelect={handleAssetSelect} />
          </div>
        </aside>

        {/* Editor Area */}
        <main className="flex-1 bg-zinc-950 flex flex-col relative">
          {selectedAssetKey ? (
            <Editor
              height="100%"
              defaultLanguage="html"
              language="html" // Monaco doesn't have built-in liquid, HTML works reasonably well for coloring
              theme="vs-dark"
              value={editorContent}
              onChange={(value) => setEditorContent(value || '')}
              options={{
                minimap: { enabled: false },
                fontSize: 14,
                wordWrap: 'on',
                formatOnPaste: true,
              }}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              Select a file to edit
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function FolderSection({ title, assets, selectedKey, onSelect }: { title: string, assets: ThemeAsset[], selectedKey: string | null, onSelect: (a: ThemeAsset) => void }) {
  if (!assets.length) return null;
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 px-2 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
        <Folder className="h-3 w-3" />
        {title}
      </div>
      <div className="mt-1 space-y-0.5">
        {assets.map(asset => {
          const filename = asset.key.split('/').pop();
          return (
            <button
              key={asset.id}
              onClick={() => onSelect(asset)}
              className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md transition-colors ${
                selectedKey === asset.key 
                  ? 'bg-primary text-primary-foreground font-medium' 
                  : 'text-foreground hover:bg-muted'
              }`}
            >
              <File className="h-4 w-4 shrink-0 opacity-70" />
              <span className="truncate">{filename}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
